import { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { useGetOfferSelectionData } from './useGetOfferSelectionData';
import { useGetVendorOffers } from './useGetVendorOffers';

/**
 * Manages the debounced vendor → offer → selection-data cascade shared by
 * ParityNotifications and LowInventoryNotifications forms.
 */
export function useVendorOfferSelection(vendorIdValue: string) {
  const { enqueueSnackbar } = useSnackbar();
  const [debouncedVendorId, setDebouncedVendorId] = useState('');
  const [selectedOfferId, setSelectedOfferId] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedVendorId(vendorIdValue?.trim() ?? ''), 600);
    return () => clearTimeout(timer);
  }, [vendorIdValue]);

  const vendorEnabled = debouncedVendorId.length > 0;

  const {
    data: offersData,
    isFetching: offersFetching,
    error: offersError,
  } = useGetVendorOffers(debouncedVendorId, vendorEnabled);

  const {
    data: selectionData,
    isFetching: selectionFetching,
    error: selectionError,
  } = useGetOfferSelectionData(selectedOfferId, debouncedVendorId, selectedOfferId.length > 0 && vendorEnabled);

  useEffect(() => {
    if (offersError) {
      enqueueSnackbar(`Failed to load vendor offers: ${offersError.message}`, { variant: 'error' });
    }
  }, [offersError, enqueueSnackbar]);

  useEffect(() => {
    if (selectionError) {
      enqueueSnackbar(`Failed to load offer selection data: ${selectionError.message}`, { variant: 'error' });
    }
  }, [selectionError, enqueueSnackbar]);

  function reset() {
    setDebouncedVendorId('');
    setSelectedOfferId('');
  }

  return {
    debouncedVendorId,
    vendorEnabled,
    offers: offersData?.result ?? [],
    offersFetching,
    selectionData,
    selectionFetching,
    lookupLoading: offersFetching || selectionFetching,
    selectedOfferId,
    setSelectedOfferId,
    reset,
  };
}
